/**
 * ymm-selects.js
 * ---------------
 * Native Partify-owned cascading YMM selector module.
 * Phase 2 — feature-flagged, coexists with EasySearch runtime.
 *
 * Activation: controlled by window.__gv2Config.useNativeYMM (default false).
 * No data is fetched and no UI is rendered unless explicitly mounted via init().
 *
 * Data required (generated by scripts/export-ymm-metaobjects.mjs):
 *   assets/ymm-index.json     – { years: ["2025","2024",...], generatedAt: "ISO" }
 *   assets/ymm-YYYY.json      – { year: "YYYY", vehicles: [{ make, model, submodel, engine }] }
 *
 * Public API (window.PartifyYMM):
 *   init(container, options)  → instance
 *   commitVehicle(vehicle)    → writes storage + dispatches partify:vehicle:changed
 *
 * Instance API:
 *   getSelected()             → { year, make, model, submodel, engine } | null
 *   preselect(vehicle)        → sets dropdown values from a vehicle object
 *   reset()                   → clears all selects, re-populates year from cache
 *   commit()                  → calls commitVehicle(getSelected()) + onCommit callback
 *   isReady()                 → bool — true when year+make+model are all selected
 */
(function () {
	"use strict";

	/* ── Adapter constants (fall back to hardcoded values if adapter not loaded) ── */
	const DELIMITER =
		(window.PartifyAdapter && window.PartifyAdapter.DELIMITER) ||
		"easysearch-preselect-delimiter";
	const SK = (window.PartifyAdapter && window.PartifyAdapter.STORAGE_KEYS) || {
		searchTerms: "searchTerms",
		fitmentPreselect: "easysearch-preselect-fitment",
	};

	/* ── JSON cache shared across instances ── */
	const _cache = {};

	function loadJson(url) {
		if (_cache[url]) return Promise.resolve(_cache[url]);
		return fetch(url)
			.then(function (r) {
				if (!r.ok) throw new Error("[PartifyYMM] HTTP " + r.status + " " + url);
				return r.json();
			})
			.then(function (data) {
				_cache[url] = data;
				return data;
			});
	}

	/* ── Storage helpers ── */

	function readTerms() {
		try {
			return JSON.parse(localStorage.getItem(SK.searchTerms)) || [];
		} catch (_) {
			return [];
		}
	}

	function buildFitmentValue(v) {
		return [v.year, v.make, v.model, v.submodel, v.engine]
			.filter(Boolean)
			.map(function (f) {
				return f + DELIMITER;
			})
			.join("");
	}

	/**
	 * Commit a selected vehicle to localStorage and dispatch events.
	 * Uses global Partify functions when available; falls back to direct writes.
	 */
	function commitVehicle(v) {
		if (!v || !v.year || !v.make || !v.model) {
			console.warn("[PartifyYMM] commitVehicle: incomplete vehicle", v);
			return;
		}
		const ymm = [v.year, v.make, v.model, v.submodel, v.engine]
			.filter(Boolean)
			.join(" ");
		const full = Object.assign({}, v, { ymm: ymm });

		/* Write searchTerms */
		if (typeof window.moveVehicleToFront === "function") {
			window.moveVehicleToFront(full);
		} else if (typeof window.setSearchTerms === "function") {
			const cur = readTerms().filter(function (t) {
				return (t.ymm || "").trim() !== ymm;
			});
			cur.unshift(full);
			window.setSearchTerms(cur.slice(0, 5), "ymm-selects");
		} else {
			/* Bare fallback — should not be needed in production */
			const cur = readTerms().filter(function (t) {
				return (t.ymm || "").trim() !== ymm;
			});
			cur.unshift(full);
			try {
				localStorage.setItem(SK.searchTerms, JSON.stringify(cur.slice(0, 5)));
			} catch (_) {}
			window.dispatchEvent(new Event("searchTermsUpdated"));
		}

		/* Write fitment preselect (preserves EasySearch page-restore contract) */
		const fitObj = {
			value: buildFitmentValue(full),
			expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
		};
		if (typeof window.setFitmentObject === "function") {
			window.setFitmentObject(fitObj);
		} else {
			try {
				localStorage.setItem(SK.fitmentPreselect, JSON.stringify(fitObj));
			} catch (_) {}
		}

		/* Dispatch canonical vehicle-changed event */
		window.dispatchEvent(
			new CustomEvent("partify:vehicle:changed", {
				detail: {
					year: full.year || "",
					make: full.make || "",
					model: full.model || "",
					submodel: full.submodel || "",
					engine: full.engine || "",
					vin: full.vin || null,
					paintCode: full.paintCode || null,
					ymm: ymm,
					source: "ymm-selects",
				},
			}),
		);
	}

	/* ── DOM helpers ── */

	function uniqSorted(arr) {
		const seen = Object.create(null);
		const out = [];
		for (let i = 0; i < arr.length; i++) {
			const v = arr[i];
			if (!seen[v]) {
				seen[v] = true;
				out.push(v);
			}
		}
		return out.sort();
	}

	function normalizeYmmText(value) {
		return String(value || "")
			.replace(/\s+/g, " ")
			.trim()
			.toLowerCase();
	}

	function sanitizeYears(years) {
		if (!Array.isArray(years)) return [];
		return uniqSorted(
			years
				.map(function (year) {
					return String(year || "").trim();
				})
				.filter(function (year) {
					return /^\d{4}$/.test(year);
				}),
		).sort(function (a, b) {
			return Number(b) - Number(a);
		});
	}

	function sanitizeVehicleRules(vehicles) {
		if (!Array.isArray(vehicles)) return [];
		const seen = Object.create(null);
		const out = [];
		vehicles.forEach(function (vehicle) {
			const rule = {
				year: String((vehicle && vehicle.year) || "").trim(),
				make: String((vehicle && vehicle.make) || "").trim(),
				model: String((vehicle && vehicle.model) || "").trim(),
				submodel: String((vehicle && vehicle.submodel) || "").trim(),
				engine: String((vehicle && vehicle.engine) || "").trim(),
			};
			if (!/^\d{4}$/.test(rule.year) || !rule.make || !rule.model) return;
			const key = [
				rule.year,
				rule.make.toLowerCase(),
				rule.model.toLowerCase(),
				rule.submodel.toLowerCase(),
				rule.engine.toLowerCase(),
			].join("|");
			if (seen[key]) return;
			seen[key] = true;
			out.push(rule);
		});
		return out;
	}

	function buildCandidateVehicle(yearValue, mk, mdl, smk, eng) {
		const vehicle = {
			year: String(yearValue || "").trim(),
			make: String(mk || "").trim(),
			model: String(mdl || "").trim(),
			submodel: String(smk || "").trim(),
			engine: String(eng || "").trim(),
		};
		vehicle.ymm = [
			vehicle.year,
			vehicle.make,
			vehicle.model,
			vehicle.submodel,
			vehicle.engine,
		]
			.filter(Boolean)
			.join(" ");
		return vehicle;
	}

	function candidatePassesFilter(allowedRules, candidateFilter, vehicle, context) {
		if (!allowedRules.length || !candidateFilter) return true;
		try {
			return candidateFilter(vehicle, context || {}) !== false;
		} catch (_) {
			return true;
		}
	}

	function globalEnginesForSubFromVehicles(vehicleList, mk, mdl, smk) {
		const makeKey = normalizeYmmText(mk);
		const modelKey = normalizeYmmText(mdl);
		const submodelKey = normalizeYmmText(smk);
		return uniqSorted(
			(Array.isArray(vehicleList) ? vehicleList : [])
				.filter(function (v) {
					return (
						normalizeYmmText(v.make) === makeKey &&
						normalizeYmmText(v.model) === modelKey &&
						(!submodelKey || normalizeYmmText(v.submodel) === submodelKey)
					);
				})
				.map(function (v) {
					return v.engine || "";
				})
				.filter(Boolean),
		);
	}

	function scopedEnginesForRules(rules) {
		return uniqSorted(
			(Array.isArray(rules) ? rules : [])
				.map(function (rule) {
					return rule.engine;
				})
				.filter(Boolean),
		);
	}

	function engineCandidatesForRules(vehicleList, mk, mdl, smk, rules) {
		const scopedEngines = scopedEnginesForRules(rules);
		if (scopedEngines.length) return scopedEngines;
		return globalEnginesForSubFromVehicles(vehicleList, mk, mdl, smk);
	}

	function submodelHasAllowedPath(options) {
		const opts = options || {};
		const yearValue = String(opts.year || "").trim();
		const mk = String(opts.make || "").trim();
		const mdl = String(opts.model || "").trim();
		const smk = String(opts.submodel || "").trim();
		const rules = Array.isArray(opts.rules) ? opts.rules : [];
		const allowedRules = Array.isArray(opts.allowedVehicleRules)
			? opts.allowedVehicleRules
			: [];
		const scopedEngines = scopedEnginesForRules(rules);
		const engines = scopedEngines.length
			? scopedEngines
			: globalEnginesForSubFromVehicles(opts.vehicles, mk, mdl, smk);
		if (engines.length) {
			return engines.some(function (eng) {
				return candidatePassesFilter(
					allowedRules,
					opts.vehicleCandidateFilter,
					buildCandidateVehicle(yearValue, mk, mdl, smk, eng),
					{
						field: "submodel",
						engineSource: scopedEngines.length ? "row" : "global",
					},
				);
			});
		}
		return candidatePassesFilter(
			allowedRules,
			opts.vehicleCandidateFilter,
			buildCandidateVehicle(yearValue, mk, mdl, smk, ""),
			{ field: "submodel", engineSource: "none" },
		);
	}

	function getActiveRulesForSelection(allowedVehicles, yearValue, mk, mdl) {
		return sanitizeVehicleRules(allowedVehicles).filter(function (rule) {
			return (
				(!yearValue || rule.year === yearValue) &&
				rule.make === mk &&
				rule.model === mdl
			);
		});
	}

	function filterAllowedSubmodels(options) {
		const opts = options || {};
		const yearValue = String(opts.year || "").trim();
		const mk = String(opts.make || "").trim();
		const mdl = String(opts.model || "").trim();
		const allowedRules = sanitizeVehicleRules(opts.allowedVehicles);
		const rules = getActiveRulesForSelection(
			opts.allowedVehicles,
			yearValue,
			mk,
			mdl,
		);
		const submodels = Array.isArray(opts.submodels)
			? opts.submodels
			: rules.map(function (rule) {
					return rule.submodel;
				});
		return uniqSorted(submodels.filter(Boolean)).filter(function (smk) {
			const rulesForSubmodel = rules.filter(function (rule) {
				return rule.submodel === smk;
			});
			return submodelHasAllowedPath({
				year: yearValue,
				make: mk,
				model: mdl,
				submodel: smk,
				rules: rulesForSubmodel,
				allowedVehicleRules: allowedRules,
				vehicles: opts.vehicles,
				vehicleCandidateFilter: opts.vehicleCandidateFilter,
			});
		});
	}

	function filterAllowedEngines(options) {
		const opts = options || {};
		const yearValue = String(opts.year || "").trim();
		const mk = String(opts.make || "").trim();
		const mdl = String(opts.model || "").trim();
		const smk = String(opts.submodel || "").trim();
		const allowedRules = sanitizeVehicleRules(opts.allowedVehicles);
		const rules = getActiveRulesForSelection(
			opts.allowedVehicles,
			yearValue,
			mk,
			mdl,
		).filter(function (rule) {
			return !smk || rule.submodel === smk;
		});
		const engineRules = rules.filter(function (rule) {
			return !!rule.engine;
		});
		const engineSource = engineRules.length ? "row" : "global";
		return engineCandidatesForRules(opts.vehicles, mk, mdl, smk, rules).filter(
			function (eng) {
				return candidatePassesFilter(
					allowedRules,
					opts.vehicleCandidateFilter,
					buildCandidateVehicle(yearValue, mk, mdl, smk, eng),
					{ field: "engine", engineSource: engineSource },
				);
			},
		);
	}

	function hasOption(sel, value) {
		if (!sel || !value) return false;
		return Array.from(sel.options).some(function (option) {
			return option.value === value;
		});
	}

	function selectIfAvailable(field, value) {
		const cleanValue = String(value || "").trim();
		if (!field || !cleanValue || !hasOption(field.sel, cleanValue)) return false;
		field.sel.value = cleanValue;
		field.sel.dispatchEvent(new Event("change", { bubbles: true }));
		return true;
	}

	function selectWhenAvailable(field, value, attempts, delay, callback) {
		const cleanValue = String(value || "").trim();
		if (!cleanValue) {
			if (callback) callback(true);
			return;
		}
		let attempt = 0;
		const trySelect = function () {
			attempt += 1;
			if (selectIfAvailable(field, cleanValue)) {
				if (callback) callback(true);
				return;
			}
			if (attempt >= attempts) {
				if (callback) callback(false);
				return;
			}
			setTimeout(trySelect, delay);
		};
		trySelect();
	}

	function fillSelect(sel, values, placeholder, disabled) {
		const prev = sel.value;
		sel.innerHTML = "";

		const def = document.createElement("option");
		def.value = "";
		def.textContent = placeholder;
		def.disabled = true;
		def.hidden = true;
		sel.appendChild(def);

		for (let i = 0; i < values.length; i++) {
			const o = document.createElement("option");
			o.value = values[i];
			o.textContent = values[i];
			sel.appendChild(o);
		}

		sel.disabled = !!disabled || values.length === 0;
		if (prev && values.indexOf(prev) >= 0) sel.value = prev;
		else sel.value = "";

	}

	function openField(sel) {
		if (!sel.disabled) sel.focus();
	}

	function makeField(id, labelText) {
		const wrap = document.createElement("div");
		wrap.className = "partify-ymm-field";
		wrap.dataset.partifyYmmField = id;

		const lbl = document.createElement("label");
		lbl.htmlFor = "partify-ymm-" + id;
		lbl.className = "partify-ymm-label";
		lbl.textContent = labelText;

		/* Use the platform picker at every screen size. */
		const sel = document.createElement("select");
		sel.id = "partify-ymm-" + id;
		sel.className = "partify-ymm-select";
		sel.disabled = true;

		const def = document.createElement("option");
		def.value = "";
		def.textContent = "— " + labelText + " —";
		def.disabled = true;
		def.hidden = true;
		sel.appendChild(def);

		wrap.appendChild(lbl);
		wrap.appendChild(sel);
		return { wrap: wrap, sel: sel };
	}

	/* ── Instance factory ── */

	/**
	 * Mount a native YMM selector into `container`.
	 *
	 * @param {HTMLElement} container
	 * @param {object} [options]
	 *   baseUrl        {string}   – base URL for ymm-YYYY.json files
	 *                               (auto-resolved from window.__PARTIFY_YMM_BASE__)
	 *   indexUrl       {string}   – override for ymm-index.json URL
	 *   onReadyChange  {function(bool)} – fired when year+make+model completeness changes
	 *   onCommit       {function(vehicle)} – fired after commitVehicle() succeeds
	 *
	 * @returns {object} instance — getSelected, preselect, reset, commit, isReady, container
	 */
	function init(container, options) {
		if (!container) {
			console.warn("[PartifyYMM] init(): container is null — skipping mount");
			return null;
		}

		const opts = options || {};

		/* Resolve base URL for year files */
		const proxy = opts.proxyUrl || window.__PARTIFY_YMM_PROXY__ || "";
		const indexUrl = opts.indexUrl || proxy + "/ymm-index";
		const onReadyChange = opts.onReadyChange || null;
		const onCommit = opts.onCommit || null;
		const vehicleCandidateFilter =
			typeof opts.vehicleCandidateFilter === "function"
				? opts.vehicleCandidateFilter
				: null;
		const _autoAdvance = opts.autoAdvance !== false; /* default true */
		const _autoSubmit = !!opts.autoSubmit;
		const _mobileAutoOpen = !!opts.mobileAutoOpen;

		let _ready = false;
		let _yearData = null; /* loaded { year, vehicles: [...] } */
		let _yearIndexYears = [];
		let _allowedYears = sanitizeYears(opts.allowedYears);
		let _allowedVehicleRules = sanitizeVehicleRules(opts.allowedVehicles);
		let _pendingYearDraft = null;
		let _pendingPreselect = null;
		let _preselectActive = false;
		let _subsAvailable = false;
		let _engsAvailable = false;

		/* Inject mobile label-hiding CSS once */
		if (!document.getElementById("partify-ymm-mobile-style")) {
			const _mobileStyle = document.createElement("style");
			_mobileStyle.id = "partify-ymm-mobile-style";
			_mobileStyle.textContent =
				"@media(max-width:768px){.partify-ymm-label{display:none}}";
			document.head.appendChild(_mobileStyle);
		}

		/* Build select fields */
		const year = makeField("year", "Year");
		const make = makeField("make", "Make");
		const model = makeField("model", "Model");
		const sub = makeField("submodel", "Sub Model");
		const engine = makeField("engine", "Engine");

		container.innerHTML = "";
		container.setAttribute("role", "group");
		container.setAttribute("aria-label", "Select your vehicle");
		[year, make, model, sub, engine].forEach(function (f) {
			container.appendChild(f.wrap);
		});

		/* Optional/non-required fields get softer placeholder */
		sub.sel.innerHTML = "";
		const subDef = document.createElement("option");
		subDef.value = "";
		subDef.textContent = "— Sub Model (optional) —";
		sub.sel.appendChild(subDef);

		engine.sel.innerHTML = "";
		const engDef = document.createElement("option");
		engDef.value = "";
		engDef.textContent = "— Engine (optional) —";
		engine.sel.appendChild(engDef);

		/* ── Ready state ── */
		function setReady(val) {
			if (_ready === val) return;
			_ready = val;
			if (onReadyChange) onReadyChange(_ready);
			if (_ready && _autoSubmit && !_preselectActive) {
				setTimeout(function () {
					if (_ready) commit();
				}, 300);
			}
		}

		function checkReady() {
			if (!year.sel.value || !make.sel.value || !model.sel.value) {
				setReady(false);
				return;
			}
			const subDone = !_subsAvailable || !!sub.sel.value;
			const engDone = !_engsAvailable || !!engine.sel.value;
			setReady(subDone && engDone);
		}

		/* ── Cascade data helpers ── */
		function filteredYears() {
			if (!_allowedYears.length) return _yearIndexYears;
			return _yearIndexYears.filter(function (yearValue) {
				return _allowedYears.indexOf(yearValue) >= 0;
			});
		}

		function fillYearSelect() {
			const years = filteredYears();
			fillSelect(year.sel, years, "— Year —", years.length === 0);
		}

		function resetDependentSelects() {
			_yearData = null;
			_subsAvailable = false;
			_engsAvailable = false;
			fillSelect(make.sel, [], "— Make —", true);
			fillSelect(model.sel, [], "— Model —", true);
			fillSelect(sub.sel, [], "— Sub Model (optional) —", true);
			fillSelect(engine.sel, [], "— Engine (optional) —", true);
			setReady(false);
		}

		function setAllowedYears(years) {
			_allowedYears = sanitizeYears(years);
			fillYearSelect();
			if (year.sel.value && filteredYears().indexOf(year.sel.value) < 0) {
				year.sel.value = "";
				resetDependentSelects();
			}
		}

		function clearAllowedYears() {
			_allowedYears = [];
			fillYearSelect();
		}

		function setAllowedVehicleRules(vehicles) {
			_allowedVehicleRules = sanitizeVehicleRules(vehicles);
		}

		function clearAllowedVehicleRules() {
			_allowedVehicleRules = [];
		}

		function activeVehicleRules() {
			if (!_allowedVehicleRules.length) return [];
			const selectedYear = year.sel.value;
			return _allowedVehicleRules.filter(function (rule) {
				return !selectedYear || rule.year === selectedYear;
			});
		}

		function queuePreselect(vehicle) {
			_pendingPreselect = vehicle ? Object.assign({}, vehicle) : null;
		}

		function applyQueuedPreselect() {
			if (!_pendingPreselect || !_yearIndexYears.length) return;
			const vehicle = _pendingPreselect;
			_pendingPreselect = null;
			preselect(vehicle);
		}

		function applyPendingDraftForYear() {
			if (!_pendingYearDraft || !_yearData) return false;
			const draft = _pendingYearDraft;
			const hasMake = !!draft.make;
			const hasModel = !!draft.model;

			if (!hasMake) {
				_pendingYearDraft = null;
				return false;
			}

			_preselectActive = true;
			selectWhenAvailable(make, draft.make, 16, 60, function (makeSelected) {
				if (!makeSelected) {
					_preselectActive = false;
					return;
				}

				if (!hasModel) {
					_pendingYearDraft = null;
					checkReady();
					_preselectActive = false;
					return;
				}

				selectWhenAvailable(model, draft.model, 16, 60, function (modelSelected) {
					if (!modelSelected) {
						_preselectActive = false;
						return;
					}

					selectWhenAvailable(
						sub,
						draft.submodel,
						16,
						60,
						function (submodelSelected) {
							if (draft.submodel && !submodelSelected) {
								_preselectActive = false;
								return;
							}

							selectWhenAvailable(
								engine,
								draft.engine,
								16,
								60,
								function (engineSelected) {
									if (draft.engine && !engineSelected) {
										_preselectActive = false;
										return;
									}
									_pendingYearDraft = null;
									checkReady();
									_preselectActive = false;
								},
							);
						},
					);
				});
			});

			return true;
		}

		function vehicles() {
			return (_yearData && _yearData.vehicles) || [];
		}

		function makesForYear() {
			const rules = activeVehicleRules();
			if (_allowedVehicleRules.length) {
				return uniqSorted(
					rules
						.map(function (rule) {
							return rule.make;
						})
						.filter(Boolean),
				);
			}
			return uniqSorted(
				vehicles()
					.map(function (v) {
						return v.make;
					})
					.filter(Boolean),
			);
		}

		function modelsForMake(mk) {
			const rules = activeVehicleRules().filter(function (rule) {
				return rule.make === mk;
			});
			if (_allowedVehicleRules.length) {
				return uniqSorted(
					rules
						.map(function (rule) {
							return rule.model;
						})
						.filter(Boolean),
				);
			}
			return uniqSorted(
				vehicles()
					.filter(function (v) {
						return v.make === mk;
					})
					.map(function (v) {
						return v.model;
					})
					.filter(Boolean),
			);
		}

		function submodelsForModel(mk, mdl) {
			const rules = activeVehicleRules().filter(function (rule) {
				return rule.make === mk && rule.model === mdl;
			});
			const submodelRules = rules.filter(function (rule) {
				return !!rule.submodel;
			});
			if (_allowedVehicleRules.length) {
				return filterAllowedSubmodels({
					year: year.sel.value,
					make: mk,
					model: mdl,
					submodels: submodelRules.map(function (rule) {
						return rule.submodel;
					}),
					allowedVehicles: _allowedVehicleRules,
					vehicles: vehicles(),
					vehicleCandidateFilter: vehicleCandidateFilter,
				});
			}
			return uniqSorted(
				vehicles()
					.filter(function (v) {
						return v.make === mk && v.model === mdl;
					})
					.map(function (v) {
						return v.submodel || "";
					})
					.filter(Boolean),
			);
		}

		function enginesForSub(mk, mdl, smk) {
			if (_allowedVehicleRules.length) {
				return filterAllowedEngines({
					year: year.sel.value,
					make: mk,
					model: mdl,
					submodel: smk,
					allowedVehicles: _allowedVehicleRules,
					vehicles: vehicles(),
					vehicleCandidateFilter: vehicleCandidateFilter,
				});
			}
			return globalEnginesForSubFromVehicles(vehicles(), mk, mdl, smk);
		}

		/* ── Cascade event handlers ── */

		year.sel.addEventListener("change", function () {
			const y = this.value;
			resetDependentSelects();
			fillSelect(make.sel, [], "— Make —", true);
			fillSelect(model.sel, [], "— Model —", true);
			fillSelect(sub.sel, [], "— Sub Model (optional) —", true);
			fillSelect(engine.sel, [], "— Engine (optional) —", true);
			setReady(false);
			if (!y) return;
			loadJson(proxy + "/ymm-data/" + y)
				.then(function (data) {
					_yearData = data;
					fillSelect(make.sel, makesForYear(), "— Make —", false);
					if (applyPendingDraftForYear()) return;
					if (_autoAdvance && !_preselectActive) {
						setTimeout(function () {
							openField(make.sel);
						}, 0);
					}
				})
				.catch(function (err) {
					_preselectActive = false;
					console.warn(
						"[PartifyYMM] Year data load failed for " + y + ":",
						err,
					);
				});
		});

		make.sel.addEventListener("change", function () {
			const mk = this.value;
			_subsAvailable = false;
			_engsAvailable = false;
			fillSelect(model.sel, [], "— Model —", true);
			fillSelect(sub.sel, [], "— Sub Model (optional) —", true);
			fillSelect(engine.sel, [], "— Engine (optional) —", true);
			setReady(false);
			if (!mk || !_yearData) return;
			fillSelect(model.sel, modelsForMake(mk), "— Model —", false);
			if (_autoAdvance && !_preselectActive) {
				setTimeout(function () {
					openField(model.sel);
				}, 0);
			}
		});

		model.sel.addEventListener("change", function () {
			const mk = make.sel.value;
			const mdl = this.value;
			fillSelect(sub.sel, [], "— Sub Model (optional) —", true);
			fillSelect(engine.sel, [], "— Engine (optional) —", true);
			_subsAvailable = false;
			_engsAvailable = false;
			if (!mk || !mdl || !_yearData) {
				checkReady();
				return;
			}
			const subs = submodelsForModel(mk, mdl);
			_subsAvailable = subs.length > 0;
			if (subs.length) {
				fillSelect(sub.sel, subs, "— Sub Model (optional) —", false);
				if (_autoAdvance && !_preselectActive) {
					setTimeout(function () {
						openField(sub.sel);
					}, 0);
				}
			}
			checkReady();
		});

		sub.sel.addEventListener("change", function () {
			const mk = make.sel.value;
			const mdl = model.sel.value;
			const smk = this.value;
			fillSelect(engine.sel, [], "— Engine (optional) —", true);
			_engsAvailable = false;
			if (!mk || !mdl || !_yearData) {
				checkReady();
				return;
			}
			const engs = enginesForSub(mk, mdl, smk);
			_engsAvailable = engs.length > 0;
			if (engs.length === 1) {
				// Only one engine — auto-select it
				fillSelect(engine.sel, engs, "— Engine (optional) —", false);
				engine.sel.value = engs[0];
				engine.sel.dispatchEvent(new Event("change", { bubbles: true }));
			} else if (engs.length > 1) {
				fillSelect(engine.sel, engs, "— Engine (optional) —", false);
				if (_autoAdvance && !_preselectActive) {
					setTimeout(function () {
						openField(engine.sel);
					}, 0);
				}
			}
			checkReady();
		});

		engine.sel.addEventListener("change", checkReady);

		/* ── Load year index ── */
		loadJson(indexUrl)
			.then(function (index) {
				const years = (index.years || [])
					.filter(function (y) {
						return /^\d{4}$/.test(y);
					})
					.sort(function (a, b) {
						return Number(b) - Number(a);
					});
				fillSelect(year.sel, years, "— Year —", false);
				_yearIndexYears = years;
				fillYearSelect();
				applyQueuedPreselect();
				if (_mobileAutoOpen && !year.sel.value) {
					setTimeout(function () {
						openField(year.sel);
					}, 400);
				}
			})
			.catch(function (err) {
				console.warn(
					"[PartifyYMM] Index load failed — run export-ymm-metaobjects.mjs first. Details:",
					err,
				);
			});

		/* ── Instance public API ── */

		function getSelected() {
			if (!year.sel.value || !make.sel.value || !model.sel.value) return null;
			return {
				year: year.sel.value,
				make: make.sel.value,
				model: model.sel.value,
				submodel: sub.sel.value || "",
				engine: engine.sel.value || "",
			};
		}

		function preselect(v) {
			if (!v) return;
			const shouldPrefillVehicle = v.requireExplicitSelections !== true;

			const incomingRules = sanitizeVehicleRules(v.allowedVehicles);
			if (incomingRules.length) {
				setAllowedVehicleRules(incomingRules);
			} else if (Array.isArray(v.allowedYears) && v.make && v.model) {
				setAllowedVehicleRules(
					sanitizeYears(v.allowedYears).map(function (allowedYear) {
						return {
							year: allowedYear,
							make: String(v.make || "").trim(),
							model: String(v.model || "").trim(),
							submodel: "",
							engine: "",
						};
					}),
				);
			} else {
				clearAllowedVehicleRules();
			}

			if (Array.isArray(v.allowedYears)) setAllowedYears(v.allowedYears);
			else if (_allowedVehicleRules.length) {
				setAllowedYears(
					_allowedVehicleRules.map(function (rule) {
						return rule.year;
					}),
				);
			} else clearAllowedYears();

			_pendingYearDraft =
				shouldPrefillVehicle && (v.make || v.model || v.submodel || v.engine)
					? {
							make: String(v.make || "").trim(),
							model: String(v.model || "").trim(),
							submodel: String(v.submodel || "").trim(),
							engine: String(v.engine || "").trim(),
					}
					: null;

			const cleanYear = shouldPrefillVehicle ? String(v.year || "").trim() : "";
			if (!cleanYear) {
				_preselectActive = false;
				if (year.sel.value) year.sel.value = "";
				resetDependentSelects();
				return;
			}

			if (!_yearIndexYears.length) {
				queuePreselect(v);
				return;
			}

			if (filteredYears().indexOf(cleanYear) < 0) {
				_preselectActive = false;
				resetDependentSelects();
				return;
			}

			_preselectActive = !!_pendingYearDraft;
			year.sel.value = cleanYear;
			year.sel.dispatchEvent(new Event("change", { bubbles: true }));
		}

		function reset() {
			_pendingYearDraft = null;
			_pendingPreselect = null;
			_allowedYears = [];
			clearAllowedVehicleRules();
			_yearData = null;
			_subsAvailable = false;
			_engsAvailable = false;
			setReady(false);
			const cached = _cache[indexUrl];
			const years = cached
				? (cached.years || [])
						.filter(function (y) {
							return /^\d{4}$/.test(y);
						})
						.sort(function (a, b) {
							return Number(b) - Number(a);
						})
				: _yearIndexYears;
			_yearIndexYears = years;
			fillSelect(year.sel, years, "— Year —", years.length === 0);
			fillSelect(make.sel, [], "— Make —", true);
			fillSelect(model.sel, [], "— Model —", true);
			fillSelect(sub.sel, [], "— Sub Model (optional) —", true);
			fillSelect(engine.sel, [], "— Engine (optional) —", true);
		}

		function commit() {
			const sel = getSelected();
			if (!sel) return;
			commitVehicle(sel);
			if (onCommit) onCommit(sel);
		}

		return {
			getSelected: getSelected,
			preselect: preselect,
			reset: reset,
			setAllowedYears: setAllowedYears,
			clearAllowedYears: clearAllowedYears,
			setAllowedVehicleRules: setAllowedVehicleRules,
			clearAllowedVehicleRules: clearAllowedVehicleRules,
			commit: commit,
			openFirst: function () {
				openField(year.sel);
			},
			isReady: function () {
				return _ready;
			},
			container: container,
		};
	}

	/* ── Public API ── */
	window.PartifyYMM = {
		init: init,
		commitVehicle: commitVehicle,
		filterAllowedSubmodels: filterAllowedSubmodels,
		filterAllowedEngines: filterAllowedEngines,
	};
})();
